/**
 * Centralized Booster Selection Controller
 *
 * Manages booster selection logic, warning modals, and form synchronization
 * between desktop and mobile product forms.
 *
 * Usage:
 *   window.boosterController.handleBoosterChange(checkbox, onConfirm)
 *   window.boosterController.setSyncing(true/false)
 */

class BoosterSelectionController {
  constructor() {
    this.isSyncing = false;
    this.modal = null;
    this.modalContent = null;
    this.pendingSelection = null;
    this.isConfirming = false;
    this._dntChecked = false;
    this._dntHealth = false;
  }

  /**
   * Should health-inferrable data be masked? (MHMDA compliance)
   * Priority: _region_debug (non-main themes only) → _dnt_health cookie → Privy geo cookie.
   */
  dntHealth() {
    if (!this._dntChecked) {
      this._dntHealth = false;
      let resolved = false;

      // 1. Dev-only debug cookie (ignored on production theme)
      if (window.Shopify?.theme?.role !== 'main') {
        const debugMatch = document.cookie.match(/(?:^|; )_region_debug=([^;]*)/);
        if (debugMatch) {
          this._dntHealth = decodeURIComponent(debugMatch[1]) === 'WA';
          resolved = true;
        }
      }

      // 2. _dnt_health — set by checkout pixel when billing address is WA
      if (!resolved) {
        const dntMatch = document.cookie.match(/(?:^|; )_dnt_health=([^;]*)/);
        if (dntMatch) {
          this._dntHealth = true;
          resolved = true;
        }
      }

      // 3. Privy geo cookie
      if (!resolved) {
        const privyMatch = document.cookie.match(/_privy_[A-Z0-9]+=([^;]+)/);
        if (privyMatch) {
          try {
            const data = JSON.parse(decodeURIComponent(privyMatch[1]));
            this._dntHealth = data.region_code === 'US_WA';
          } catch { this._dntHealth = false; }
        }
      }

      this._dntChecked = true;
    }
    return this._dntHealth;
  }

  /**
   * Send a gtag event, stripping booster_name when health-data masking is active.
   */
  trackEvent(eventName, params) {
    if (!window.gtag) return;
    if (this.dntHealth()) {
      const { booster_name, ...safeParams } = params;
      gtag('event', eventName, safeParams);
    } else {
      gtag('event', eventName, params);
    }
  }

  /**
   * Initialize the warning modal
   * Called by booster-warning-modal.liquid
   */
  initializeModal(modal, modalContent) {
    this.modal = modal;
    this.modalContent = modalContent;
  }

  /**
   * Check if a booster needs a warning and handle the selection
   *
   * @param {HTMLInputElement} checkbox - The booster checkbox
   * @param {Function} onConfirm - Callback to execute after warning confirmed (or immediately if no warning)
   * @returns {boolean} - true if selection should proceed immediately, false if blocked by warning
   */
  handleBoosterChange(checkbox, onConfirm) {
    // Skip warning check if we're programmatically syncing forms
    if (this.isSyncing) {
      onConfirm();
      return true;
    }

    // Only check warnings when checking a booster (not unchecking)
    if (!checkbox.checked) {
      // Track booster deselection in GA4
      const boosterLabel = checkbox.closest('[data-booster]');
      const boosterKey = boosterLabel ? boosterLabel.dataset.booster : null;
      if (boosterKey) {
        this.trackEvent('select_booster', {
          event_category: 'pdp_selection',
          booster_name: boosterKey,
          action: 'remove'
        });
      }
      onConfirm();
      return true;
    }

    // Find the booster key from the label
    const boosterLabel = checkbox.closest('[data-booster]');
    const boosterKey = boosterLabel ? boosterLabel.dataset.booster : null;

    if (!boosterKey) {
      onConfirm();
      return true;
    }

    // Check if this booster has a warning
    const needsWarning = this.checkWarning(boosterKey, () => {
      // Warning confirmed - execute the callback
      this.isSyncing = true;
      checkbox.checked = true;
      onConfirm();
      // Reset sync flag after a brief delay
      setTimeout(() => {
        this.isSyncing = false;
      }, 50);
    });

    if (needsWarning) {
      // Warning shown - don't proceed with selection yet
      return false;
    }

    // No warning needed - proceed immediately
    // Track booster selection in GA4
    if (boosterKey) {
      this.trackEvent('select_booster', {
        event_category: 'pdp_selection',
        booster_name: boosterKey,
        action: 'add'
      });
    }
    onConfirm();
    return true;
  }

  /**
   * Check if a booster needs a warning and show modal if necessary
   *
   * @param {string} boosterKey - The booster key (e.g., "metabolism")
   * @param {Function} callback - Function to call when warning is confirmed
   * @returns {boolean} - true if warning shown, false if no warning needed
   */
  checkWarning(boosterKey, callback) {
    // Skip warning if we're in the middle of confirming
    if (this.isConfirming) {
      return false;
    }

    const hasWarning = document.querySelector(`[data-booster-warning="${boosterKey}"]`);
    if (hasWarning) {
      // Find the checkbox for this booster
      const boosterLabel = document.querySelector(`[data-booster="${boosterKey}"]`);
      const checkbox = boosterLabel ? boosterLabel.querySelector('input[type="checkbox"]') : null;

      this.pendingSelection = {
        key: boosterKey,
        callback: callback,
        checkbox: checkbox
      };
      this.openModal(boosterKey);
      return true; // Warning shown, prevent immediate selection
    }
    return false; // No warning, allow immediate selection
  }

  /**
   * Open the warning modal for a specific booster
   */
  openModal(boosterKey) {
    if (!this.modal || !this.modalContent) return;

    const template = document.querySelector(`[data-booster-warning="${boosterKey}"]`);
    if (!template) return;

    this.modalContent.innerHTML = template.innerHTML;
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Track booster warning modal view in GA4
    this.trackEvent('view_booster_warning', {
      event_category: 'pdp_engagement',
      booster_name: boosterKey
    });
  }

  /**
   * Close the warning modal
   */
  closeModal() {
    if (!this.modal) return;

    // If modal is already closed, don't do anything (prevents double-close)
    if (!this.modal.classList.contains('active')) return;

    const pendingSelectionKey = this.pendingSelection?.key;
    const wasConfirming = this.isConfirming;

    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    this.pendingSelection = null;
    this.isConfirming = false;

    // Only track close if user didn't accept (canceled/closed without accepting)
    if (!wasConfirming) {
      this.trackEvent('close_booster_warning', {
        event_category: 'pdp_engagement',
        booster_name: pendingSelectionKey
      });
    }
  }

  /**
   * Confirm the booster selection (called when user clicks continue in modal)
   */
  confirmBooster() {
    if (this.pendingSelection) {
      this.isConfirming = true; // Set flag before confirming

      // Track booster warning confirmation in GA4
      this.trackEvent('accept_booster_warning', {
        event_category: 'pdp_engagement',
        booster_name: this.pendingSelection.key
      });

      // Track booster selection in GA4
      this.trackEvent('select_booster', {
        event_category: 'pdp_selection',
        booster_name: this.pendingSelection.key,
        action: 'add'
      });

      // Trigger the actual booster selection
      const event = new CustomEvent('booster-warning-confirmed', {
        detail: { booster: this.pendingSelection }
      });
      document.dispatchEvent(event);
    }
    this.closeModal();
  }

  /**
   * Set the syncing flag (used by mobile modal when syncing to desktop)
   */
  setSyncing(value) {
    this.isSyncing = value;
  }

  /**
   * Get the current syncing state
   */
  isSyncingState() {
    return this.isSyncing;
  }
}

// Initialize global controller instance
if (!window.boosterController) {
  window.boosterController = new BoosterSelectionController();
}

// Expose legacy global functions for backward compatibility
if (!window.checkBoosterWarning) {
  window.checkBoosterWarning = function(boosterKey, callback) {
    return window.boosterController.checkWarning(boosterKey, callback);
  };
}

if (!window.setBoosterSyncing) {
  window.setBoosterSyncing = function(value) {
    window.boosterController.setSyncing(value);
  };
}
